import * as THREE from "three";
import { LevelSim } from "@highmarch/domain";
import type { EnemyView } from "./enemyView";
import { hqNode } from "./common";

/**
 * The Spellward's rune shield (docs/ENEMIES_V2.md): a faint cyan bubble of runes round every Spellward, always there
 * (its trait), that flares bright and ripples when magic strikes it and glances off (MagicResisted) — so the player
 * sees a Frost, Lightning, Poison or Arcane hit being turned aside, and reaches for arrows, shot and shells. A model
 * that ships its own `shield` node also kicks that node on the flare.
 */
export const RUNE_SHIELD = {
  color: [0.3, 0.9, 1.0] as [number, number, number],
  /** Resting and flared strength, the flare's decay (per second), the bubble's size (× the unit's height) and lift. */
  rest: 0.22, flare: 1.0, decay: 3.2, size: 0.62, lift: 0.5,
  /** The model's `shield` node's kick on a flare (scale). */
  kick: 0.18,
} as const;

const VERT = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying vec3 vP;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vN = normalize(mat3(modelMatrix) * normal);
  vV = normalize(cameraPosition - w.xyz);
  vP = position;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
const FRAG = /* glsl */ `
uniform float uA; uniform float uF; uniform float uT; uniform vec3 uC;
varying vec3 vN; varying vec3 vV; varying vec3 vP;
void main() {
  float fres = pow(1.0 - abs(dot(vN, vV)), 2.2);
  // a hex-ish rune lattice: two crossed bands of stripes turning slowly, sharpened
  float a = atan(vP.z, vP.x), h = vP.y;
  float lat = abs(sin(a * 9.0 + uT * 0.6)) * abs(sin(h * 9.0 - uT * 0.4));
  float runes = smoothstep(0.82, 0.95, lat);
  // the flare's ripple runs up from where the bolts strike (the bottom of the bubble)
  float ripple = exp(-pow((h + 1.0 - uF * 2.2) / 0.25, 2.0)) * uF;
  float m = fres * (0.6 + 0.8 * uF) + runes * (0.25 + 0.75 * uF) + ripple;
  gl_FragColor = vec4(uC * (1.0 + 1.8 * uF), clamp(m * uA, 0.0, 1.0));
}`;

interface Shield { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; flare: number; seen: boolean; node: THREE.Object3D | null; nodeScale: number }

let sphere: THREE.BufferGeometry | null = null;

export class RuneShields {
  readonly group = new THREE.Group();
  private readonly shields = new Map<number, Shield>();
  private time = 0;
  /** Set by the owner: the live view of an enemy, and its drawn body middle. */
  viewOf: (id: number) => EnemyView | undefined = () => undefined;
  body: (id: number) => THREE.Vector3 | null = () => null;

  constructor(private readonly sim: LevelSim, private readonly reduced: () => boolean) {}

  /** Magic glanced off `id` (MagicResisted): its shield flares. */
  flare(id: number): void { const s = this.shields.get(id); if (s) s.flare = 1; }

  update(dt: number): void {
    this.time += dt;
    const sim = this.sim, reduced = this.reduced();
    for (const s of this.shields.values()) s.seen = false;
    for (const e of sim.enemySlots) {
      if (!e.alive || sim.balance.enemies[e.archetype].abilities.magicTaken.toNumber() >= 1) continue;
      const v = this.viewOf(e.id), at = this.body(e.id);
      if (!v || !at) continue;
      let s = this.shields.get(e.id);
      if (!s) {
        sphere ??= new THREE.IcosahedronGeometry(1, 3);
        const mat = new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
          uniforms: { uA: { value: RUNE_SHIELD.rest }, uF: { value: 0 }, uT: { value: 0 }, uC: { value: new THREE.Vector3(...RUNE_SHIELD.color) } } });
        const mesh = new THREE.Mesh(sphere, mat);
        mesh.renderOrder = 7;
        this.group.add(mesh);
        const name = v.model.replace(/^hq:/, "").replace(/_lod1$/, "");
        const node = v.hq ? hqNode(v.body, name, "shield") : null;
        s = { mesh, mat, flare: 0, seen: true, node, nodeScale: node ? node.scale.x : 1 };
        this.shields.set(e.id, s);
      }
      s.seen = true;
      s.flare = Math.max(0, s.flare - RUNE_SHIELD.decay * dt);
      const h = Math.max(0.3, v.headNow || v.headY);
      s.mesh.position.set(at.x, at.y, at.z);
      s.mesh.scale.set(h * RUNE_SHIELD.size * 1.05, h * RUNE_SHIELD.size * 1.2, h * RUNE_SHIELD.size * 1.05);
      const u = s.mat.uniforms;
      u.uF.value = s.flare;
      u.uA.value = (RUNE_SHIELD.rest + (RUNE_SHIELD.flare - RUNE_SHIELD.rest) * s.flare) * Math.min(1, v.age / 0.4);
      u.uT.value = reduced ? 0 : this.time;
      if (s.node) s.node.scale.setScalar(s.nodeScale * (1 + RUNE_SHIELD.kick * s.flare));
    }
    for (const [id, s] of this.shields) {
      if (s.seen) continue;
      this.group.remove(s.mesh); s.mat.dispose();
      this.shields.delete(id);
    }
  }

  dispose(): void { for (const s of this.shields.values()) s.mat.dispose(); this.shields.clear(); this.group.clear(); }
}
